/**
 * FractionalCounsel.jsx
 * ---------------------------------------------------------------------------
 * Section 7. Fractional GC positioning, three-node engagement diagram
 * (Business → CaseSignalHQ → Counsel Network), core value list, and a
 * two-column services grid.
 */

import React from "react";
import Section from "../ui/Section.jsx";
import { H2, Lede } from "../ui/Heading.jsx";
import Button from "../ui/Button.jsx";
import { tokens } from "../../lib/tokens.js";

const CORE_VALUE = [
  "Ongoing legal guidance without full-time overhead",
  "Access to specialized expertise as needed",
  "Scalable legal support aligned with business growth",
];

const SERVICES = [
  "Contract drafting and review",
  "Risk assessment and mitigation",
  "Compliance strategy",
  "Corporate governance guidance",
  "Strategic advisory for transactions",
  "Operational legal counsel",
];

export default function FractionalCounsel() {
  return (
    <Section id="counsel" label="VI · Fractional Counsel">
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 96,
          alignItems: "start",
        }}
      >
        {/* Left column */}
        <div>
          <H2>Fractional general counsel for growing businesses.</H2>
          <Lede>
            Businesses increasingly require sophisticated legal support without
            the cost or rigidity of full-time in-house counsel.
          </Lede>
          <Lede>
            CaseSignalHQ provides access to experienced legal professionals on
            a flexible, on-demand basis.
          </Lede>

          <div
            style={{
              marginTop: 64,
              padding: 32,
              background: tokens.paper,
              border: `1px solid ${tokens.divider}`,
            }}
          >
            <div
              style={{
                fontFamily: "'Inter', sans-serif",
                fontSize: 11,
                letterSpacing: "0.22em",
                textTransform: "uppercase",
                color: tokens.inkSoft,
                marginBottom: 28,
              }}
            >
              Engagement Model
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 32px 1fr 32px 1fr",
                alignItems: "center",
                gap: 0,
              }}
            >
              <Node label="Business" sub="Operating entity" />
              <Arrow />
              <Node label="CaseSignalHQ" sub="Orchestration" emphasis />
              <Arrow />
              <Node label="Counsel Network" sub="Vetted attorneys" />
            </div>
          </div>

          <div style={{ marginTop: 48 }}>
            <Button
              onClick={() =>
                document
                  .getElementById("firms")
                  ?.scrollIntoView({ behavior: "smooth" })
              }
            >
              Request Consultation
            </Button>
          </div>
        </div>

        {/* Right column */}
        <div>
          <div
            style={{
              fontFamily: "'Inter', sans-serif",
              fontSize: 11,
              letterSpacing: "0.22em",
              textTransform: "uppercase",
              color: tokens.inkSoft,
              marginBottom: 24,
            }}
          >
            Core Value
          </div>
          <div style={{ marginBottom: 48 }}>
            {CORE_VALUE.map((t, i) => (
              <div
                key={t}
                style={{
                  padding: "20px 0",
                  borderTop: `1px solid ${tokens.divider}`,
                  borderBottom:
                    i === CORE_VALUE.length - 1
                      ? `1px solid ${tokens.divider}`
                      : "none",
                  fontFamily: "'Cormorant Garamond', serif",
                  fontSize: 22,
                  color: tokens.ink,
                  fontWeight: 400,
                  letterSpacing: "-0.005em",
                  lineHeight: 1.35,
                }}
              >
                {t}
              </div>
            ))}
          </div>

          <div
            style={{
              fontFamily: "'Inter', sans-serif",
              fontSize: 11,
              letterSpacing: "0.22em",
              textTransform: "uppercase",
              color: tokens.inkSoft,
              marginBottom: 24,
            }}
          >
            Services
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 0,
            }}
          >
            {SERVICES.map((s) => (
              <div
                key={s}
                style={{
                  padding: "20px 16px 20px 0",
                  borderTop: `1px solid ${tokens.divider}`,
                  fontFamily: "'Inter', sans-serif",
                  fontSize: 14,
                  color: tokens.ink,
                  lineHeight: 1.5,
                }}
              >
                {s}
              </div>
            ))}
          </div>
        </div>
      </div>
    </Section>
  );
}

// ---------------------------------------------------------------------------
function Node({ label, sub, emphasis }) {
  return (
    <div
      style={{
        border: `1px solid ${emphasis ? tokens.ink : tokens.hairline}`,
        background: emphasis ? tokens.ink : tokens.bg,
        color: emphasis ? "#fff" : tokens.ink,
        padding: "20px 16px",
        textAlign: "center",
      }}
    >
      <div
        style={{
          fontFamily: "'Cormorant Garamond', serif",
          fontSize: 18,
          fontWeight: 500,
          marginBottom: 4,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontFamily: "'Inter', sans-serif",
          fontSize: 10,
          letterSpacing: "0.16em",
          textTransform: "uppercase",
          color: emphasis ? "rgba(255,255,255,0.6)" : tokens.inkSoft,
        }}
      >
        {sub}
      </div>
    </div>
  );
}

function Arrow() {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <svg width="32" height="12" viewBox="0 0 32 12" aria-hidden>
        <line x1="0" y1="6" x2="26" y2="6" stroke={tokens.ink} strokeWidth="1" />
        <path
          d="M 22 1 L 30 6 L 22 11"
          fill="none"
          stroke={tokens.ink}
          strokeWidth="1"
        />
      </svg>
    </div>
  );
}
